import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { RED_DEER, TILE_ATTRIBUTION, TILE_URL } from "./StopMap";

// A CSS pin in the tag's colour (no image files, so nothing for the bundler to lose).
function pinIcon(color) {
  const safe = /^#[0-9a-fA-F]{6}$/.test(color || "") ? color : "#b23a2e";
  return L.divIcon({ className: "lm-pin", html: `<span style="background:${safe}"></span>`, iconSize: [24, 24], iconAnchor: [12, 29] });
}

export const RADIUS_MIN_M = 10;
export const RADIUS_MAX_M = 150;
const CIRCLES_FROM_ZOOM = 16;
const MAX_CIRCLES = 1500;
// Leaflet's own earth radius, so the handle sits exactly on the drawn circle.
const M_PER_DEG = (L.CRS.Earth.R * Math.PI) / 180;

function clampRadius(m) {
  return Math.min(RADIUS_MAX_M, Math.max(RADIUS_MIN_M, Math.round(m)));
}

function eastOf(lat, lng, metres) {
  return [lat, lng + metres / (M_PER_DEG * Math.max(0.01, Math.cos((lat * Math.PI) / 180)))];
}

const HANDLE_ICON = L.divIcon({ className: "lm-radius-handle", html: "<span></span>", iconSize: [16, 16], iconAnchor: [8, 8] });

// Landmarks as dots in their tag colour. The landmark being edited is shown
// as a pin instead; it can be dragged when `pinDraggable` is on. While
// `placing`, a click anywhere on the map calls onPlace with that position.
// `pinRadius` (metres) draws the cleaning distance around the pin; with
// `radiusEditable` a handle on its edge can be dragged to grow or shrink it,
// calling onRadiusChange. Zoomed in, every landmark's distance is drawn too.
export default function LandmarkMap({ points, colorFor, selectedId, pin, pinColor, pinDraggable, pinRadius, radiusEditable, placing, focusToken, onSelect, onPlace, onPinMove, onRadiusChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const dotsRef = useRef(null);
  const rendererRef = useRef(null);
  const pinRef = useRef(null);
  const fittedRef = useRef(false);
  const handlersRef = useRef({});
  const pinPosRef = useRef(null);
  const pinColorRef = useRef(null);
  const radiusRendererRef = useRef(null);
  const circlesRef = useRef(null);
  const pinCircleRef = useRef(null);
  const handleRef = useRef(null);
  const drawCirclesRef = useRef(() => {});

  useEffect(() => {
    handlersRef.current = { onSelect, onPlace, onPinMove, onRadiusChange, placing, pinRadius };
    pinPosRef.current = pin;
  });

  useEffect(() => {
    const container = containerRef.current;
    const map = L.map(container, { preferCanvas: true, zoomControl: true }).setView(RED_DEER, 12);
    L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(map);
    // Distance circles sit in their own pane under the dots, so dots stay clickable.
    map.createPane("lm-radius");
    map.getPane("lm-radius").style.zIndex = 350;
    radiusRendererRef.current = L.canvas({ padding: 0.5, pane: "lm-radius" });
    circlesRef.current = L.layerGroup().addTo(map);
    rendererRef.current = L.canvas({ padding: 0.5 });
    dotsRef.current = L.layerGroup().addTo(map);
    map.on("moveend zoomend", () => drawCirclesRef.current());
    map.on("click", (e) => {
      const h = handlersRef.current;
      if (h.placing && h.onPlace) h.onPlace(e.latlng.lat, e.latlng.lng);
    });
    mapRef.current = map;
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => map.invalidateSize());
      observer.observe(container);
    }
    return () => {
      if (observer) observer.disconnect();
      map.remove();
      mapRef.current = null;
      pinRef.current = null;
      pinCircleRef.current = null;
      handleRef.current = null;
    };
  }, []);

  // Every landmark's cleaning distance, once zoomed in far enough to see it.
  useEffect(() => {
    drawCirclesRef.current = () => {
      const map = mapRef.current;
      const layer = circlesRef.current;
      if (!map || !layer) return;
      layer.clearLayers();
      if (map.getZoom() < CIRCLES_FROM_ZOOM) return;
      const bounds = map.getBounds().pad(0.2);
      let drawn = 0;
      for (const p of points) {
        if (drawn >= MAX_CIRCLES) break;
        if (p.id === selectedId || !p.radius_m || !bounds.contains([p.lat, p.lng])) continue;
        L.circle([p.lat, p.lng], {
          renderer: radiusRendererRef.current,
          radius: p.radius_m,
          color: colorFor(p),
          weight: p.custom_rule ? 2 : 1,
          dashArray: p.custom_rule ? null : "4 4",
          fillColor: colorFor(p),
          fillOpacity: 0.08,
          interactive: false,
        }).addTo(layer);
        drawn += 1;
      }
    };
    drawCirclesRef.current();
  }, [points, selectedId, colorFor]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (placing) L.DomUtil.addClass(container, "lm-map-placing");
    else L.DomUtil.removeClass(container, "lm-map-placing");
  }, [placing]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = dotsRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    // Dashed lines join landmarks that are cleaned together.
    const sets = {};
    points.forEach((p) => {
      if (p.link_id) (sets[p.link_id] = sets[p.link_id] || []).push(p);
    });
    Object.values(sets).forEach((members) => {
      if (members.length < 2) return;
      const [first, ...rest] = members;
      rest.forEach((m) => {
        L.polyline([[first.lat, first.lng], [m.lat, m.lng]], {
          renderer: rendererRef.current, color: "#1e3a4c", weight: 2, dashArray: "4 4", opacity: 0.85, interactive: false,
        }).addTo(layer);
      });
    });
    points.forEach((p) => {
      if (p.id === selectedId) return;
      const dot = L.circleMarker([p.lat, p.lng], {
        renderer: rendererRef.current,
        radius: p.removed ? 4 : 6,
        color: "#ffffff",
        weight: 1.5,
        fillColor: colorFor(p),
        fillOpacity: p.removed ? 0.45 : 0.95,
      });
      dot.bindTooltip(p.name || "No name", { direction: "top", offset: [0, -6] });
      dot.on("click", (e) => {
        L.DomEvent.stopPropagation(e);
        const h = handlersRef.current;
        if (h.placing) {
          if (h.onPlace) h.onPlace(e.latlng.lat, e.latlng.lng);
        } else if (h.onSelect) {
          h.onSelect(p.id);
        }
      });
      dot.addTo(layer);
    });
    if (!fittedRef.current && points.length) {
      map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [24, 24], maxZoom: 15 });
      fittedRef.current = true;
    }
  }, [points, selectedId, colorFor]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!pin) {
      if (pinRef.current) {
        pinRef.current.remove();
        pinRef.current = null;
      }
      return;
    }
    if (!pinRef.current) {
      const marker = L.marker([pin.lat, pin.lng], { icon: pinIcon(pinColor), draggable: true, autoPan: true, keyboard: false, zIndexOffset: 1000 });
      marker.on("drag", () => {
        // Keep the distance circle and its handle with the pin while it moves.
        const ll = marker.getLatLng();
        const r = handlersRef.current.pinRadius;
        if (pinCircleRef.current) pinCircleRef.current.setLatLng(ll);
        if (handleRef.current && r) handleRef.current.setLatLng(eastOf(ll.lat, ll.lng, r));
      });
      marker.on("dragend", () => {
        const ll = marker.getLatLng();
        const h = handlersRef.current;
        if (h.onPinMove) h.onPinMove(ll.lat, ll.lng);
      });
      marker.addTo(map);
      pinRef.current = marker;
      pinColorRef.current = pinColor;
    } else {
      if (pinColorRef.current !== pinColor) {
        pinRef.current.setIcon(pinIcon(pinColor));
        pinColorRef.current = pinColor;
      }
      const current = pinRef.current.getLatLng();
      if (current.lat !== pin.lat || current.lng !== pin.lng) pinRef.current.setLatLng([pin.lat, pin.lng]);
    }
    if (pinDraggable) pinRef.current.dragging.enable();
    else pinRef.current.dragging.disable();
  }, [pin, pinColor, pinDraggable]);

  // The pin's distance circle, and the handle that resizes it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!pin || !pinRadius) {
      if (pinCircleRef.current) pinCircleRef.current.remove();
      if (handleRef.current) handleRef.current.remove();
      pinCircleRef.current = null;
      handleRef.current = null;
      return;
    }
    const color = /^#[0-9a-fA-F]{6}$/.test(pinColor || "") ? pinColor : "#b23a2e";
    if (!pinCircleRef.current) {
      pinCircleRef.current = L.circle([pin.lat, pin.lng], {
        renderer: radiusRendererRef.current, radius: pinRadius, color, weight: 2, fillColor: color, fillOpacity: 0.15, interactive: false,
      }).addTo(map);
    } else {
      pinCircleRef.current.setLatLng([pin.lat, pin.lng]);
      pinCircleRef.current.setRadius(pinRadius);
      pinCircleRef.current.setStyle({ color, fillColor: color });
    }
    if (!handleRef.current) {
      const handle = L.marker(eastOf(pin.lat, pin.lng, pinRadius), { icon: HANDLE_ICON, draggable: true, keyboard: false, zIndexOffset: 1100 });
      handle.bindTooltip("", { permanent: true, direction: "right", offset: [10, 0], className: "lm-radius-label" });
      handle.on("drag", () => {
        const centre = pinPosRef.current;
        if (!centre || !pinCircleRef.current) return;
        const r = clampRadius(map.distance([centre.lat, centre.lng], handle.getLatLng()));
        pinCircleRef.current.setRadius(r);
        handle.setTooltipContent(`${r} m`);
      });
      handle.on("dragend", () => {
        const centre = pinPosRef.current;
        if (!centre) return;
        const r = clampRadius(map.distance([centre.lat, centre.lng], handle.getLatLng()));
        handle.setLatLng(eastOf(centre.lat, centre.lng, r));
        const h = handlersRef.current;
        if (h.onRadiusChange) h.onRadiusChange(r);
      });
      handle.addTo(map);
      handleRef.current = handle;
    } else {
      handleRef.current.setLatLng(eastOf(pin.lat, pin.lng, pinRadius));
    }
    handleRef.current.setTooltipContent(`${Math.round(pinRadius)} m`);
    if (radiusEditable) {
      handleRef.current.dragging.enable();
      L.DomUtil.removeClass(handleRef.current.getElement(), "lm-radius-handle-off");
    } else {
      handleRef.current.dragging.disable();
      L.DomUtil.addClass(handleRef.current.getElement(), "lm-radius-handle-off");
    }
  }, [pin, pinRadius, pinColor, radiusEditable]);

  useEffect(() => {
    const map = mapRef.current;
    const p = pinPosRef.current;
    if (!map || !focusToken || !p) return;
    map.setView([p.lat, p.lng], Math.max(map.getZoom(), 17));
  }, [focusToken]);

  return <div ref={containerRef} className="map lm-map" role="region" aria-label="Map of landmarks" />;
}
